import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { PowerPlant } from '../../../common/components/solar-calculation-result/model/power-plant';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalysisService } from '../../../services/analysis.service';
import { CompanyService } from '../../../services/company.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ImplementationPlanRequest } from '../../../common/models/analysis/implementation-plan-request';
import { LoaderService } from '../../../services/loader.service';
import { finalize, forkJoin, mergeMap, tap } from 'rxjs';
import { UnsubscribeOnDestroyAdapter } from '../../../common/adapters/unsubscibe-on-destroy.adapter';
import { ToastService } from '../../../services/toast.service';
import { CompanyType } from '../../../common/models/company/company-type.enum';
import { Company } from '../../../common/models/company/company';
import { OfferStatus } from '../../../common/models/company/offer-status.enum';
import { CompanyWithOffer } from '../../../common/models/company/company-with-offer';
import { CompanyImplementationData } from '../../../common/models/analysis/company-implementation-data';
import { PageTitleService } from '../../../services/page-title.service';

@Component({
  selector: 'psc-implementation-plan',
  templateUrl: './implementation-plan.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImplementationPlanComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  powerPlant: PowerPlant;

  lineClass = 'col-span-3 border my-6 w-[90%] justify-self-center';
  banks: Company[] = [];
  contractors: Company[] = [];
  selectedBanks: CompanyWithOffer[] = [];
  selectedContractors: CompanyWithOffer[] = [];
  maxCompanyCount: number = 3;

  formGroup: FormGroup;
  showAddContractorDialog: boolean;
  showAddBankDialog: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analysisService: AnalysisService,
    private companyService: CompanyService,
    private formBuilder: FormBuilder,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastService: ToastService,
    private pageTitleService: PageTitleService,
  ) {
    super();
    /**
     * t(title.user.implementation-plan)
     */
    this.pageTitleService.setTitle('title.user.implementation-plan');
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id')!;

    this.formGroup = this.formBuilder.group({
      eligibleForGridConnection: [false],
      optimalPowerLessThanConnectedPower: [false],
      contactByCompanyAccepted: [false],
      loanFinancingForSolarPlantDesired: [false],
      conceptualProjectNeeded: [false],
      companyAuthorizedForNextStepInConnection: [false],
    });

    this.loaderService.showLoader();
    this.analysisService.getSavedPowerPlant(id).pipe(
      finalize(() => {
        this.loaderService.hideLoader();
      }),
      mergeMap((powerPlant) => {
        this.powerPlant = powerPlant;
        this.formGroup.patchValue(powerPlant);
        this.formGroup.updateValueAndValidity();
        this.pageTitleService.setTitle('title.user.implementation-plan', { plantName: this.powerPlant.projectName });
        this.changeDetectorRef.detectChanges();
        return forkJoin([
          this.getBanks(),
          this.getContractors(),
        ]);
      }),
    ).subscribe({
      next: ([banks, contractors]) => {
        this.selectedBanks = banks
          .filter(bank => this.powerPlant.banks.find(bankData => bankData.companyId === bank.companyId))
          .map(bank => new CompanyWithOffer(bank, this.powerPlant.banks.find(bankData => bankData.companyId === bank.companyId)));
        this.selectedContractors = contractors
          .filter(contractor => this.powerPlant.contractors.find(contractorData => contractorData.companyId === contractor.companyId))
          .map(contractor => new CompanyWithOffer(contractor, this.powerPlant.contractors.find(contractorData => contractorData.companyId === contractor.companyId)));
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.router.navigate(['/404']);
      },
    });

    this.subs.sink = this.formGroup.controls['loanFinancingForSolarPlantDesired'].valueChanges.subscribe((value) => {
      if (!value) {
        this.selectedBanks = [];
      }
    });
  }

  getBanks() {
    return this.companyService.getCompanies(CompanyType.BANK, this.powerPlant.price.country).pipe(
      tap((banks) => {
        this.banks = banks;
      }));
  }

  getContractors() {
    return this.companyService.getCompanies(CompanyType.COMPANY, this.powerPlant.price.country).pipe(
      tap((contractors) => {
        this.contractors = contractors;
      }));
  }

  addContractor(contractor: Company) {
    this.selectedContractors.push(new CompanyWithOffer(contractor, this.emptyCompanyImplementationData(contractor.companyId)));
  }

  addBank(bank: Company) {
    this.selectedBanks.push(new CompanyWithOffer(bank, this.emptyCompanyImplementationData(bank.companyId)));
  }

  listIncludes(list: CompanyWithOffer[], company: Company) {
    return list.findIndex(comp => comp.company.companyId == company.companyId) > -1;
  }

  saveImplementationPlan() {
    const request: ImplementationPlanRequest = {
      analysisId: this.powerPlant.analysisId,
      banks: this.selectedBanks.map(bank => bank.implementationCompanyData),
      contractors: this.selectedContractors.map(contractor => contractor.implementationCompanyData),
      ...this.formGroup.value,
    };

    this.loaderService.showLoader();
    this.analysisService.saveImplementationPlan(request).pipe(
      finalize(() => this.loaderService.hideLoader()),
    ).subscribe(() => {
      this.toastService.showSuccessToast();
      this.router.navigate(['customer/saved-power-plants']);
    });
  }

  removeBank(company: CompanyWithOffer) {
    this.selectedBanks = this.selectedBanks.filter(bank => bank.company.companyId !== company.company.companyId);
  }

  removeContractor(company: CompanyWithOffer) {
    this.selectedContractors = this.selectedContractors.filter(contractor => contractor.company.companyId !== company.company.companyId);
  }

  emptyCompanyImplementationData(companyId: string) {
    return {
      companyId: companyId,
      offerAmount: null,
      offerStatus: OfferStatus.WAITING,
    } as CompanyImplementationData;
  }
}
